import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo } from 'react'

import { Pagination, Table } from 'slab'
import { useGetPartnersPartnerIdPayouts } from 'utils/web_api'

import DownloadEligibilityReportButton from './DownloadEligibilityReportButton'
import DownloadPayoutSummaryButton from './DownloadPayoutSummaryButton'
import { getLocaleDateString, handleStatusHover } from '../../utils'
import TableSkeleton from '../reusable/table/Skeleton'
import { TableSearchSingleColumn } from '../reusable/table/TableSearchSingleColumn'

// Payout types - defined locally since the API returns polymorphic entities
// that OpenAPI can't fully represent
interface Partner {
  id: string
  display_name: string
  friendly_name?: string
  support_email?: string
  support_phone_number?: string
}

interface Money {
  formatted_amount: string
  subunits: number
  currency: string
}

interface PartnersPayout {
  id: string
  amount: Money
  created_at: string
  description: string | null
  type: string
  // Type-specific fields from polymorphic entities
  employee_count?: number
  employer_id?: string
  employer_name?: string
  employer_dba?: string
  month?: string
  monthly_commission?: Money
  outbound_payment_id?: string
  source_partner?: Partner
  destination_partner?: Partner
}

interface PayoutsResponse {
  payouts: PartnersPayout[]
}

const PAGE_SIZE = 20
const COLUMN_COUNT = 5

function getCategoryDisplay (type: string): { classes: string; label: string; statusTip: string } {
  switch (type) {
    case 'referral_fee':
      return { classes: 'bg-green-100 text-green-800', label: 'Referral fee', statusTip: '' }
    case 'bank_transfer':
      return {
        classes: 'bg-blue-100 text-blue-600',
        label: 'Payout',
        statusTip: 'Bank transfers can take up to 5 business days to arrive in your account.'
      }
    case 'reverse_referral_fee':
      return { classes: 'bg-yellow-100 text-yellow-800', label: 'Correction', statusTip: '' }
    case 'balance_transfer':
      return { classes: 'bg-purple-100 text-purple-800', label: 'Balance transfer', statusTip: '' }
    default:
      return { classes: 'bg-gray-100 text-gray-800', label: 'Other', statusTip: '' }
  }
}

function PartnersPayoutsTable ({ partnerId }: { partnerId: string }) {
  const { data: payoutsResponse, isLoading, isError } = useGetPartnersPartnerIdPayouts<PayoutsResponse>(partnerId)
  const payouts = payoutsResponse?.payouts ?? []

  const columns: ColumnDef<PartnersPayout, unknown>[] = useMemo(() => [
    {
      accessorKey: 'created_at',
      header: 'Date',
      cell: (info) => getLocaleDateString(new Date(info.getValue() as string)),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: (info) => (info.getValue() as Money).formatted_amount,
    },
    {
      accessorKey: 'type',
      header: 'Category',
      cell: (info) => {
        const { classes, label, statusTip } = getCategoryDisplay(info.getValue() as string)
        return (
          <div
            onMouseEnter={(e) => statusTip && handleStatusHover(e, statusTip)}
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}
          >
            {label}
          </div>
        )
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      accessorKey: 'id',
      header: 'Details',
      cell: (info) => {
        const type = info.row.original.type
        const id = info.getValue() as string
        const createdAt = info.row.original.created_at

        if (type === 'referral_fee') {
          return (
            <div className='flex justify-center align-center'>
              <DownloadEligibilityReportButton
                partnerId={partnerId}
                payoutId={info.getValue() as string}
                employerName={info.row.original.employer_name}
                month={info.row.original.month}
              />
            </div>
          )
        }

        if (type === 'bank_transfer') {
          return (
            <div className='flex justify-center align-center'>
              <DownloadPayoutSummaryButton partnerId={partnerId} payoutId={id} payoutDate={createdAt} />
            </div>
          )
        }

        return null
      },
    },
  ], [partnerId])

  const table = useReactTable({
    columns,
    data: payouts,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: PAGE_SIZE,
      },
    },
  })

  const totalRows = table.getPrePaginationRowModel().rows.length

  function handlePageChange (page: number) {
    table.setPageIndex(page)
  }

  if (isLoading) {
    return (
      <div>
        <div className='flex items-center justify-between mb-3 w-full'>
          <div className='w-full sm:w-1/2 sm:max-w-sm'>
            <TableSearchSingleColumn table={table} columnId='description' placeholder='Search transaction descriptions' />
          </div>
        </div>
        <TableSkeleton columnCount={COLUMN_COUNT} rowCount={PAGE_SIZE} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className='bg-white rounded-2xl border border-gray-200 shadow-sm p-8'>
        <div className='text-center body'>
          There was an error loading this page. Please reach out to{' '}
          <a href='mailto:support@thatch.com' className='text-thatch-600 underline'>support@thatch.com</a> if you continue
          to experience this error.
        </div>
      </div>
    )
  }

  const rows = table.getRowModel().rows
  const isEmpty = rows.length === 0

  return (
    <div>
      <div className='flex items-center justify-between mb-3 w-full'>
        <div className='w-full sm:w-1/2 sm:max-w-sm'>
          <TableSearchSingleColumn table={table} columnId='description' placeholder='Search transaction descriptions' />
        </div>
      </div>
      <div className='bg-white rounded-2xl border border-gray-200 shadow-sm pt-2'>
        <Table>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.ColumnHeader key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            {isEmpty
              ? (
                <tr className='h-96' data-testid='TanstackTableMessage'>
                  <td colSpan={COLUMN_COUNT} className='body text-center'>
                    <div className='flex flex-col items-center justify-center gap-4'>
                      You haven't collected any referral fees yet. On the 15th of each month, we'll credit your account for all your active clients.
                    </div>
                  </td>
                </tr>
                )
              : (
                  rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))
                )}
          </Table.Body>
        </Table>
        <div className='flex items-center justify-between py-4 gap-2 px-6 border-t border-gray-200'>
          <div className='body-sm-bold'>
            {totalRows} {totalRows === 1 ? 'transaction' : 'transactions'}
          </div>
          {totalRows > 0 && (
            <Pagination
              pageIndex={table.getState().pagination.pageIndex}
              perPage={PAGE_SIZE}
              totalRows={totalRows}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default PartnersPayoutsTable
